<template>
  <div class="select-table">
    <v-autocomplete
      v-model="modelData"
      :items="filteredItems"
      solo
      dense
      :clearable="clearable"
      hide-details
      chips
      closable-chips
      :color="color"
      :multiple="multiple"
      :filter="() => true"
      :item-text="itemTextKey"
      class="text-center test"
      item-color="first"
      ref="autocomplete"
      :menu="menuOpen"
      @input="setParentValue()"
      @update:menu="onMenuUpdate"
      @update:search-input="updateSearchQuery"
      :menu-props="{ contentClass: 'select-grid-menu' }"
      @keydown.enter="handleEnter"
      @keydown.space="handleSpace"
    >
      <template #label>
        <label class="p-label">{{ label }}</label>
      </template>

      <template v-slot:append>
        <v-icon class="chevron-icon" size="14pt">mdi-chevron-down</v-icon>
      </template>

      <template v-slot:selection="{ item }">
        <v-chip v-if="multiple" :key="item.id" small outlined>
          {{ item[selectionKey] ?? "" }}
          <v-icon class="ms-1" small @click.stop="removeItem(item)">
            mdi-close-circle
          </v-icon>
        </v-chip>
        <span v-else class="p-fs-xs text-center">
          {{ item[selectionKey] ?? "" }}
        </span>
      </template>

      <template v-slot:prepend-item>
        <v-row class="align-center header-row-grid mb-0">
          <v-col
            v-if="multiple"
            :cols="getCheckboxColSize()"
            class="py-0"
          ></v-col>

          <v-col
            v-for="(column, index) in columns"
            :key="column.key || index"
            :cols="getColSize(index)"
            class="text-center p-fs-xs bordered-col"
            @click.stop.prevent="sortByColumn(column.key)"
            style="cursor: pointer"
          >
            <p class="mb-0">
              {{ column.name }}
              <span v-if="sortColumn === column.key">
                {{ sortAsc ? "▲" : "▼" }}
              </span>
            </p>
          </v-col>
        </v-row>
      </template>

      <template v-slot:item="{ item, attrs }">
        <v-row
          v-bind="attrs"
          class="align-center custom-item ma-0"
          style="cursor: pointer; flex-wrap: wrap"
        >
          <v-col
            v-if="multiple"
            :cols="getCheckboxColSize()"
            class="py-0 bordered-col custom-checkbox"
          >
            <v-checkbox
              :id="'test-' + item.code"
              :input-value="isSelected(item)"
              hide-details
              :color="checkboxColor"
              class="mt-0 pt-0"
            />
          </v-col>

          <v-col
            v-for="(column, index) in columns"
            :key="column.key || index"
            :cols="getColSize(index)"
            class="text-start p-fs-xs bordered-col"
          >
            <span :for="'test-' + item[column.key]">
              {{ item[column.key] }}
            </span>
          </v-col>
        </v-row>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
export default {
  name: "SelectGrid",
  props: {
    modelValue: { default: null },
    label: { type: String, default: "" },
    items: { type: Array, required: true },
    columns: { type: Array, required: true },
    color: { type: String, default: "first" },
    multiple: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    iconColor: { type: String, default: "first" },
    itemTextKey: { type: String, default: "text" },
    selectionKey: { type: String, default: "text" },
    columnSizes: { type: Array, default: () => [] },
    checkboxColor: { type: String, default: "first" },
  },
  data() {
    return {
      internalValue: this.multiple ? [] : null,
      enterPressedOnce: false,
      sortColumn: null,
      modelData: null,
      sortAsc: true,
      searchQuery: "",
      // ✅ کنترل دستی باز/بسته بودن منو
      menuOpen: false,
    };
  },
  computed: {
    filteredItems() {
      let items = this.items;
      if (this.searchQuery) {
        const q = String(this.searchQuery).toLowerCase();
        items = items.filter((item) => {
          return this.columns.some((col) => {
            const val = item[col.key];
            return val && String(val).toLowerCase().includes(q);
          });
        });
      }
      if (this.sortColumn) {
        items = [...items].sort((a, b) => {
          const valA = a[this.sortColumn];
          const valB = b[this.sortColumn];

          if (valA == null) return 1;
          if (valB == null) return -1;

          if (typeof valA === "number" && typeof valB === "number") {
            return this.sortAsc ? valA - valB : valB - valA;
          }

          return this.sortAsc
            ? String(valA).localeCompare(String(valB))
            : String(valB).localeCompare(String(valA));
        });
      }
      return items;
    },
  },
  watch: {
    modelValue: {
      handler(newVal) {
        this.modelData = newVal;
        if (this.multiple) {
          this.internalValue = Array.isArray(newVal)
            ? this.items.filter((i) => newVal.includes(i.value))
            : [];
        } else {
          this.internalValue =
            this.items.find((i) => i.value === newVal) ?? null;
        }
      },
      immediate: true,
    },
  },
  methods: {
    handleEnter(e) {
      const isOpen = this.$refs.autocomplete?.isMenuActive ?? this.menuOpen;

      if (isOpen) {
        e.stopPropagation();
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      if (this.$refs.autocomplete) {
        this.$refs.autocomplete.blur();
      }
      this.menuOpen = false;
      this.searchQuery = "";

      this.$nextTick(() => {
        setTimeout(() => {
          this.$emit("enter-pressed");
        }, 0);
      });
    },
    handleSpace(e) {
      const isOpen = this.$refs.autocomplete?.isMenuActive ?? this.menuOpen;

      if (!isOpen) {
        e.preventDefault();
        this.menuOpen = true;
      }
    },
    setParentValue() {
      const searchItem = this.filteredItems.find(
        (e) => e.value === this.modelData,
      );
      if (searchItem) {
        this.$emit("update:modelValue", searchItem);
      } else {
        this.$emit("update:modelValue", null);
      }
      this.menuOpen = false;
    },
    onMenuUpdate(isOpen) {
      this.menuOpen = isOpen;
      if (!isOpen) {
        this.searchQuery = "";
      }
    },
    sortByColumn(columnKey) {
      if (this.sortColumn === columnKey) {
        this.sortAsc = !this.sortAsc;
      } else {
        this.sortColumn = columnKey;
        this.sortAsc = true;
      }
    },
    updateModelValue() {
      let valueToEmit;

      if (this.multiple) {
        valueToEmit = this.internalValue.map(
          (v) => this.items.find((i) => i.value === (v.value ?? v)) || v,
        );
      } else {
        const val = this.internalValue?.value ?? this.internalValue;
        valueToEmit =
          this.items.find((i) => i.value === val) || this.internalValue;
      }
      this.$emit("update:modelValue", valueToEmit);
    },
    updateSearchQuery(query) {
      this.searchQuery = query;
    },
    removeItem(item) {
      if (this.multiple) {
        if (!Array.isArray(this.internalValue)) return;
        const idx = this.internalValue.indexOf(item.value ?? item);
        if (idx > -1) {
          this.internalValue.splice(idx, 1);
          this.updateModelValue();
        }
      } else {
        this.internalValue = null;
        this.updateModelValue();
      }
    },
    isSelected(item) {
      if (this.multiple) {
        return (
          Array.isArray(this.internalValue) &&
          this.internalValue.includes(item.value ?? item)
        );
      } else {
        return (
          this.internalValue &&
          (this.internalValue.value ?? this.internalValue) ===
            (item.value ?? item)
        );
      }
    },

    getCheckboxColSize() {
      const cs = this.columnSizes || [];
      const v = Number(cs[0]);
      return Number.isFinite(v) && v > 0 ? v : 1;
    },
    getColSize(index) {
      const cs = this.columnSizes || [];
      const offset = this.multiple ? 1 : 0;
      const provided = cs[index + offset];

      if (provided !== undefined && !Number.isNaN(Number(provided))) {
        const n = Number(provided);
        if (n > 0) return n;
      }
      const checkbox = this.multiple ? this.getCheckboxColSize() : 0;
      const remaining = Math.max(1, 12 - checkbox);
      const per = Math.max(
        1,
        Math.floor(remaining / Math.max(1, this.columns.length)),
      );
      return per;
    },
  },
};
</script>
